/**
 * Utility functions for lender submissions
 */

/**
 * Converts a date string to yyyy-mm-dd format
 * Handles various date formats like MM/DD/YYYY, M/D/YYYY, etc.
 * 
 * @param {string} dateStr - The date string to convert
 * @returns {string} - Date in yyyy-mm-dd format or original string if invalid
 */
export const formatDateToYYYYMMDD = (dateStr) => {
  if (!dateStr) return '';
  
  try {
    // Check if already in yyyy-mm-dd format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Check specifically for MM/YYYY format (e.g., "07/2014")
    // This is for business start dates that only have month and year
    const monthYearMatch = dateStr.match(/^(\d{1,2})\/(\d{4})$/);
    if (monthYearMatch) {
      const month = monthYearMatch[1].padStart(2, '0');
      const year = monthYearMatch[2];
      // Always use day=01 (first day of month) for MM/YYYY format
      return `${year}-${month}-01`;
    }
    
    // Handle common US format MM/DD/YYYY or M/D/YYYY
    const dateMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dateMatch) {
      const month = dateMatch[1].padStart(2, '0');
      const day = dateMatch[2].padStart(2, '0');
      const year = dateMatch[3];
      return `${year}-${month}-${day}`;
    }
    
    // Try using Date object as fallback
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0]; // Returns yyyy-mm-dd
    }
    
    // If all parsing attempts fail, return the original string
    console.warn(`Could not parse date: ${dateStr}`);
    return dateStr;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateStr;
  }
};

/**
 * Cleans and standardizes data before submitting to lenders
 * Ensures consistent formatting across all lender submissions
 * 
 * @param {Object} requestData - The original request data
 * @returns {Object} - The cleaned request data with the same structure
 */
export const cleanLenderSubmissionData = (requestData) => {
  // Create a deep copy to avoid modifying the original object
  const cleanedData = JSON.parse(JSON.stringify(requestData));
  
  // If opportunityData exists in the request (which contains business data)
  if (cleanedData.opportunityData) {
    // Clean annual revenue - remove all non-numeric characters
    if (cleanedData.opportunityData.AnnualRevenue) {
      const originalValue = cleanedData.opportunityData.AnnualRevenue;
      cleanedData.opportunityData.AnnualRevenue = 
        originalValue.toString().replace(/[^0-9]/g, '');
      
      // Log the change for development/debugging
      if (originalValue !== cleanedData.opportunityData.AnnualRevenue) {
        console.log('Cleaned AnnualRevenue:', 
          originalValue, '->', cleanedData.opportunityData.AnnualRevenue);
      }
    }
    
    // Clean average bank balance - remove all non-numeric characters
    if (cleanedData.opportunityData.AverageBankBalance) {
      const originalValue = cleanedData.opportunityData.AverageBankBalance;
      cleanedData.opportunityData.AverageBankBalance = 
        originalValue.toString().replace(/[^0-9]/g, '');
      
      // Log the change for development/debugging
      if (originalValue !== cleanedData.opportunityData.AverageBankBalance) {
        console.log('Cleaned AverageBankBalance:', 
          originalValue, '->', cleanedData.opportunityData.AverageBankBalance);
      }
    }
    
    // Standardize primary contact date of birth format to yyyy-mm-dd
    if (cleanedData.opportunityData.ContactDateOfBirth) {
      const originalValue = cleanedData.opportunityData.ContactDateOfBirth;
      cleanedData.opportunityData.ContactDateOfBirth = formatDateToYYYYMMDD(originalValue);
      
      // Log the change if needed
      if (originalValue !== cleanedData.opportunityData.ContactDateOfBirth) {
        console.log('Standardized ContactDateOfBirth format:', 
          originalValue, '->', cleanedData.opportunityData.ContactDateOfBirth);
      }
    }
    
    // Standardize secondary contact date of birth format to yyyy-mm-dd
    if (cleanedData.opportunityData.Contact2DateOfBirth) {
      const originalValue = cleanedData.opportunityData.Contact2DateOfBirth;
      cleanedData.opportunityData.Contact2DateOfBirth = formatDateToYYYYMMDD(originalValue);
      
      // Log the change if needed
      if (originalValue !== cleanedData.opportunityData.Contact2DateOfBirth) {
        console.log('Standardized Contact2DateOfBirth format:', 
          originalValue, '->', cleanedData.opportunityData.Contact2DateOfBirth);
      }
    }
    
    // Look for any other date of birth fields that might need standardization
    Object.keys(cleanedData.opportunityData).forEach(key => {
      if (key.toLowerCase().includes('dateofbirth') || 
          (key.toLowerCase().includes('date') && key.toLowerCase().includes('birth'))) {
        // Skip the ones we've already processed
        if (key === 'ContactDateOfBirth' || key === 'Contact2DateOfBirth') {
          return;
        }
        
        const originalValue = cleanedData.opportunityData[key];
        if (originalValue) {
          cleanedData.opportunityData[key] = formatDateToYYYYMMDD(originalValue);
          
          // Log the change if needed
          if (originalValue !== cleanedData.opportunityData[key]) {
            console.log(`Standardized ${key} format:`, 
              originalValue, '->', cleanedData.opportunityData[key]);
          }
        }
      }
    });
  }
  
  return cleanedData;
}; 